<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import API from 'dashboard/api/assistantConfigurations';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfigurationChanges from './components/ConfigurationChanges.vue';

const route = useRoute();
const { t } = useI18n();
const accountId = computed(() => route.params.accountId);
const workspace = ref(null);
const inboxId = ref(null);
const draft = ref(null);
const message = ref('');
const question = ref('');
const result = ref(null);
const confirmed = ref(false);
const busy = ref(false);
const loading = ref(false);
const error = ref('');
let sequence = 0;
const errorCodes = [
  'draft_changed',
  'configuration_changed',
  'invalid_configuration',
  'invalid_knowledge',
  'invalid_model_response',
  'assistant_unavailable',
  'quota_exhausted',
  'conversation_limit',
  'no_changes',
];
const showError = err => {
  const code = err.response?.data?.code;
  error.value = t(
    `AI_CONFIG.ERRORS.${errorCodes.includes(code) ? code : 'generic'}`
  );
};
const selectedInbox = computed(() =>
  workspace.value?.inboxes.find(i => i.id === inboxId.value)
);
const hasChanges = computed(
  () =>
    draft.value &&
    JSON.stringify(draft.value.original) !==
      JSON.stringify(draft.value.configuration)
);
const isDraft = computed(() => draft.value?.status === 'draft');
const statusText = status => t(`AI_CONFIG.STATUS.${status}`);

const load = async (keepDraft = false) => {
  sequence += 1;
  const ticket = sequence;
  loading.value = true;
  error.value = '';
  result.value = null;
  confirmed.value = false;
  if (!keepDraft) {
    draft.value = null;
    message.value = '';
    question.value = '';
  }
  try {
    const { data } = await API.workspace(inboxId.value);
    if (ticket !== sequence) return;
    workspace.value = data;
    if (inboxId.value !== data.inbox_id) inboxId.value = data.inbox_id;
  } catch (err) {
    if (ticket === sequence) showError(err);
  } finally {
    if (ticket === sequence) loading.value = false;
  }
};
watch(
  [accountId, inboxId],
  ([account], previous) => {
    if (previous && account !== previous[0]) {
      inboxId.value = null;
      workspace.value = null;
    }
    busy.value = false;
    load();
  },
  { immediate: true }
);

const newDraft = () => {
  draft.value = null;
  result.value = null;
  confirmed.value = false;
  error.value = '';
  message.value = '';
};
const openVersion = async id => {
  sequence += 1;
  const ticket = sequence;
  busy.value = true;
  error.value = '';
  result.value = null;
  confirmed.value = false;
  try {
    const { data } = await API.show(id);
    if (ticket === sequence) draft.value = data;
  } catch (err) {
    if (ticket === sequence) showError(err);
  } finally {
    if (ticket === sequence) busy.value = false;
  }
};
const send = async () => {
  if (!message.value.trim() || busy.value || loading.value) return;
  sequence += 1;
  const ticket = sequence;
  busy.value = true;
  error.value = '';
  result.value = null;
  confirmed.value = false;
  try {
    const { data } = isDraft.value
      ? await API.action(draft.value.id, 'message', {
          message: message.value,
          lock_version: draft.value.lock_version,
        })
      : await API.create({ inbox_id: inboxId.value, message: message.value });
    if (ticket !== sequence) return;
    draft.value = data;
    message.value = '';
    workspace.value.versions = [
      { id: data.id, status: data.status, created_at: data.created_at },
      ...workspace.value.versions.filter(v => v.id !== data.id),
    ].slice(0, 20);
  } catch (err) {
    if (ticket === sequence) showError(err);
  } finally {
    if (ticket === sequence) busy.value = false;
  }
};
const preview = async () => {
  if (!draft.value || !question.value.trim() || busy.value) return;
  sequence += 1;
  const ticket = sequence;
  busy.value = true;
  error.value = '';
  result.value = null;
  try {
    const { data } = await API.action(draft.value.id, 'preview', {
      question: question.value,
      lock_version: draft.value.lock_version,
    });
    if (ticket === sequence) result.value = data;
  } catch (err) {
    if (ticket === sequence) showError(err);
  } finally {
    if (ticket === sequence) busy.value = false;
  }
};
const publish = async action => {
  if (!confirmed.value || !draft.value || busy.value) return;
  sequence += 1;
  const ticket = sequence;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await API.action(draft.value.id, action, {
      lock_version: draft.value.lock_version,
    });
    if (ticket !== sequence) return;
    draft.value = data;
    await load(true);
    if (ticket + 1 === sequence) busy.value = false;
  } catch (err) {
    if (ticket === sequence) showError(err);
  } finally {
    if (ticket === sequence) busy.value = false;
  }
};
</script>

<template>
  <div class="w-full h-full overflow-y-auto p-6 text-n-slate-12">
    <div class="flex items-start justify-between gap-4 mb-5">
      <div>
        <p class="text-sm text-n-slate-11">{{ t('AI_CONFIG.MODULE') }}</p>
        <h1 class="text-xl font-semibold mt-3">{{ t('AI_CONFIG.TITLE') }}</h1>
        <p class="text-sm text-n-slate-11 mt-2">
          {{ t('AI_CONFIG.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="t('AI_CONFIG.NEW')"
        :disabled="busy || loading || !inboxId"
        @click="newDraft"
      />
    </div>
    <div class="flex flex-wrap gap-4 items-end mb-5">
      <label class="flex flex-col gap-2 text-sm min-w-64"
        >{{ t('AI_CONFIG.INBOX') }}
        <select v-model="inboxId" :disabled="busy || loading">
          <option
            v-for="inbox in workspace?.inboxes || []"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </label>
      <label
        v-if="workspace?.versions.length"
        class="flex flex-col gap-2 text-sm min-w-64"
        >{{ t('AI_CONFIG.HISTORY') }}
        <select
          :value="draft?.id || ''"
          :disabled="busy || loading"
          @change="openVersion(Number($event.target.value))"
        >
          <option value="" disabled>{{ t('AI_CONFIG.SELECT_VERSION') }}</option>
          <option
            v-for="version in workspace.versions"
            :key="version.id"
            :value="version.id"
          >
            #{{ version.id }} · {{ statusText(version.status) }} ·
            {{ new Date(version.created_at).toLocaleString() }}
          </option>
        </select>
      </label>
      <Button :label="t('AI_CONFIG.RELOAD')" :disabled="busy" @click="load()" />
    </div>
    <p
      v-if="error"
      role="alert"
      class="mb-4 rounded-lg bg-n-ruby-2 text-n-ruby-11 p-4 text-sm"
    >
      {{ error }}
    </p>
    <p
      v-if="selectedInbox?.external_active"
      class="mb-4 rounded-lg bg-n-amber-2 text-n-amber-11 p-4 text-sm"
    >
      {{ t('AI_CONFIG.EXTERNAL_BOT') }}
    </p>
    <p v-if="loading" class="text-sm text-n-slate-11" role="status">
      {{ t('AI_CONFIG.LOADING') }}
    </p>
    <p v-else-if="!inboxId" class="text-sm text-n-slate-11">
      {{ t('AI_CONFIG.NO_INBOX') }}
    </p>
    <div v-else class="grid gap-5 xl:grid-cols-2 items-start">
      <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
        <h2 class="font-medium">{{ t('AI_CONFIG.CHAT_TITLE') }}</h2>
        <p class="text-xs text-n-slate-11 mt-2">
          {{ t('AI_CONFIG.CHAT_HINT') }}
        </p>
        <div
          class="grid gap-4 my-5 max-h-[32rem] overflow-y-auto"
          aria-live="polite"
        >
          <p
            v-if="!draft?.messages.length"
            class="text-sm text-n-slate-11 leading-6 p-4 bg-n-alpha-1 rounded-lg"
          >
            {{ t('AI_CONFIG.EXAMPLE') }}
          </p>
          <div
            v-for="(item, index) in draft?.messages || []"
            :key="index"
            class="rounded-lg p-4 text-sm whitespace-pre-wrap break-words"
            :class="
              item.role === 'user' ? 'bg-n-blue-2 ms-6' : 'bg-n-alpha-1 me-6'
            "
          >
            <p class="text-xs font-medium mb-2 text-n-slate-11">
              {{
                item.role === 'user' ? t('AI_CONFIG.YOU') : t('AI_CONFIG.TITLE')
              }}
            </p>
            {{ item.content }}
          </div>
          <p v-if="busy" role="status" class="text-sm text-n-slate-11">
            {{ t('AI_CONFIG.WORKING') }}
          </p>
        </div>
        <form class="grid gap-3" @submit.prevent="send">
          <label class="flex flex-col gap-2 text-sm"
            >{{ t('AI_CONFIG.REQUEST') }}
            <textarea
              v-model="message"
              rows="4"
              maxlength="4000"
              :disabled="busy"
              :placeholder="t('AI_CONFIG.PLACEHOLDER')"
              required
            />
          </label>
          <Button
            type="submit"
            :label="t(isDraft ? 'AI_CONFIG.REVISE' : 'AI_CONFIG.GENERATE')"
            :disabled="busy || !message.trim()"
          />
        </form>
        <p class="text-xs text-n-slate-11 mt-3">
          {{ t('AI_CONFIG.USAGE_HINT') }}
        </p>
      </section>
      <div class="grid gap-5">
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
          <div class="flex justify-between gap-3 mb-4">
            <h2 class="font-medium">{{ t('AI_CONFIG.REVIEW') }}</h2>
            <template v-if="draft">
              <span class="text-sm text-n-blue-11">
                {{
                  t('AI_CONFIG.VERSION_BADGE', {
                    id: draft.id,
                    status: statusText(draft.status),
                  })
                }}
              </span>
            </template>
          </div>
          <ConfigurationChanges
            v-if="draft"
            :original="draft.original"
            :candidate="draft.configuration"
            :portals="workspace.portals"
          />
          <p v-else class="text-sm text-n-slate-11">
            {{ t('AI_CONFIG.REVIEW_EMPTY') }}
          </p>
          <div v-if="draft" class="mt-5 rounded-lg bg-n-alpha-1 p-4">
            <h3 class="font-medium text-sm">
              {{ draft.configuration.welcome_title }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-2 whitespace-pre-wrap">
              {{ draft.configuration.welcome_tagline }}
            </p>
            <p
              v-if="draft.configuration.greeting_enabled"
              class="text-sm whitespace-pre-wrap mt-3"
            >
              {{ t('AI_CONFIG.GREETING_PREVIEW') }}
              {{ draft.configuration.greeting_message }}
            </p>
          </div>
          <div
            v-if="draft && draft.status !== 'rolled_back'"
            class="grid gap-3 border-t border-n-weak mt-5 pt-4"
          >
            <p
              v-if="draft.status === 'applied'"
              class="text-sm text-n-slate-11"
            >
              {{ t('AI_CONFIG.ROLLBACK_HINT') }}
            </p>
            <label class="flex items-start gap-2 text-sm"
              ><input v-model="confirmed" type="checkbox" :disabled="busy" />{{
                t(isDraft ? 'AI_CONFIG.CONFIRM' : 'AI_CONFIG.CONFIRM_ROLLBACK')
              }}</label
            >
            <Button
              :label="t(isDraft ? 'AI_CONFIG.APPLY' : 'AI_CONFIG.ROLLBACK')"
              :disabled="busy || !confirmed || (isDraft && !hasChanges)"
              @click="publish(isDraft ? 'apply' : 'rollback')"
            />
          </div>
        </section>
        <section
          v-if="draft"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
        >
          <h2 class="font-medium">{{ t('AI_CONFIG.TRIAL') }}</h2>
          <p class="text-xs text-n-slate-11 mt-2">
            {{ t('AI_CONFIG.TRIAL_HINT') }}
          </p>
          <form class="grid gap-3 mt-4" @submit.prevent="preview">
            <label class="flex flex-col gap-2 text-sm"
              >{{ t('AI_CONFIG.TEST_QUESTION')
              }}<input
                v-model="question"
                type="text"
                maxlength="8000"
                :disabled="busy"
                required
            /></label>
            <Button
              type="submit"
              :label="t('AI_CONFIG.TEST')"
              :disabled="busy || !question.trim()"
            />
          </form>
          <div
            v-if="result"
            class="rounded-lg bg-n-alpha-1 p-4 mt-4 text-sm"
            role="status"
          >
            <span class="text-xs text-n-blue-11"
              >{{
                t(
                  result.handoff
                    ? 'AI_CONFIG.RESULT_HANDOFF'
                    : 'AI_CONFIG.RESULT_ANSWER'
                )
              }}
              · {{ t(`AI_CONFIG.SOURCE.${result.source}`) }}</span
            >
            <p class="mt-2 whitespace-pre-wrap break-words">
              {{ result.answer }}
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
